import {
  addDays, differenceInCalendarDays, endOfQuarter, getDaysInMonth,
  startOfDay, subDays, subMonths, subYears,
} from "date-fns";
import { OUTLOOK_HISTORY_POLICY, prepareOutlookHistoryQuality, type OutlookHistoryQualityOptions } from "./outlook-history-quality";

// A separate, empirical scenario calculation. Does not train, replace, or
// multiply Expected Close V2. Accepts the existing normalized history fields.
export type OutlookHistoryDeal = {
  id: number | string;
  createdAt: Date | null;
  closedAt: Date | null;
  isWon: boolean;
  isLost: boolean;
  value: number;
  dimensions: { segment?: string };
};

const MIN_COHORT = 30;
const MIN_WINS = 5;
const finiteDate = (date: Date | null): date is Date => Boolean(date && Number.isFinite(date.getTime()));

export function getOutlookClock(asOf: Date, range?: { start: Date; end: Date }) {
  const end = range?.end ?? endOfQuarter(asOf);
  return { asOf, end, daysRemaining: Math.max(0, differenceInCalendarDays(end, asOf)) };
}

function matureCohort(deals: readonly OutlookHistoryDeal[], asOf: Date, days: number) {
  const cutoff = new Date(asOf.getTime() - days * 86_400_000);
  const from = subMonths(startOfDay(asOf), 24);
  return deals.filter(deal => finiteDate(deal.createdAt)
    && deal.createdAt >= from && deal.createdAt < cutoff);
}

function withinHorizon(deal: OutlookHistoryDeal, days: number, asOf: Date, minimumDays: number) {
  if (!deal.isWon || !finiteDate(deal.createdAt) || !finiteDate(deal.closedAt)) return false;
  const elapsed = (deal.closedAt.getTime() - deal.createdAt.getTime()) / 86_400_000;
  return elapsed >= minimumDays && elapsed <= days && deal.closedAt < asOf;
}

export function summarizeOutlookCohort(deals: readonly OutlookHistoryDeal[], asOf: Date, days: number, minimumDays = 0) {
  const cohort = matureCohort(deals, asOf, days);
  const invalid = (deal: OutlookHistoryDeal) => deal.isWon && (
    !finiteDate(deal.closedAt) || !finiteDate(deal.createdAt)
    || deal.closedAt < deal.createdAt || deal.closedAt >= asOf
    || !Number.isFinite(deal.value) || deal.value < 0
  );
  const invalidOutcomes = cohort.filter(invalid).length;
  const eligible = cohort.filter(deal => !invalid(deal));
  const wins = eligible.filter(deal => withinHorizon(deal, days, asOf, minimumDays));
  const ready = eligible.length >= MIN_COHORT && wins.length >= MIN_WINS
    && invalidOutcomes / Math.max(1, cohort.length) <= 0.05;
  return {
    ready, population: eligible.length, wins: wins.length, invalidOutcomes,
    probability: ready ? wins.length / eligible.length : null,
    expectedArrPerCreatedDeal: ready ? wins.reduce((sum, deal) => sum + deal.value, 0) / eligible.length : null,
    dealIds: eligible.map(deal => String(deal.id)),
    winningDealIds: wins.map(deal => String(deal.id)),
  };
}

// Estimate the ARR arriving at each age of a newly created opportunity.
// Each age uses only cohorts observed for that entire day, including losses
// and still-open opportunities. Recent mature cohorts receive more weight.
// Creation seasonality is handled separately: month matching conversion mixed
// old and new sales behaviour and left sparse short closing windows unsupported.
function futureArrByAge(deals: readonly OutlookHistoryDeal[], asOf: Date, maximumDays: number,
  quality?: ReturnType<typeof prepareOutlookHistoryQuality>) {
  const lookbackDays = 365;
  const halfLifeDays = 180;
  const dayMs = 86_400_000;
  const cohort = deals.flatMap(deal => {
    if (!finiteDate(deal.createdAt)) return [];
    if ((!Number.isFinite(deal.value) || deal.value < 0) && (!quality || deal.isWon)) return [];
    const estimatedWinAges = quality?.winAges.get(String(deal.id));
    if (deal.isWon && (!finiteDate(deal.closedAt) || deal.closedAt < deal.createdAt) && !estimatedWinAges) return [];
    return [{ age: (asOf.getTime() - deal.createdAt.getTime()) / dayMs,
      winAge: deal.isWon && finiteDate(deal.closedAt) && deal.closedAt < asOf
        ? (deal.closedAt.getTime() - deal.createdAt.getTime()) / dayMs : null,
      estimatedWinAges, estimated: quality?.estimatedIds.has(String(deal.id)) ?? false,
      value: deal.isWon ? deal.value : 0, segment: deal.dimensions.segment ?? "Unspecified" }];
  });
  const ages: Array<{ arr: number; probability: number; bySegment: Record<string, number>; estimatedArr: number }> = [];
  let minimumEffectivePopulation = Infinity;
  let ready = true;
  const recentWins = cohort.filter(deal => deal.age >= 1 && deal.age < lookbackDays + 1 && deal.winAge !== null).length;
  for (let age = 0; age < Math.ceil(maximumDays); age++) {
    let weightSum = 0;
    let weightSquares = 0;
    let revenue = 0;
    let winWeight = 0;
    let estimatedRevenue = 0;
    let affected = 0;
    let population = 0;
    const bySegment: Record<string, number> = {};
    for (const deal of cohort) {
      const matureDays = deal.age - (age + 1);
      if (matureDays < 0 || matureDays >= lookbackDays) continue;
      population++;
      if (deal.estimated) affected++;
      const weight = 2 ** (-matureDays / halfLifeDays);
      weightSum += weight;
      weightSquares += weight ** 2;
      const share = deal.estimatedWinAges
        ? deal.estimatedWinAges.reduce((sum, outcome) => sum + (Math.floor(outcome.age) === age ? outcome.share : 0), 0)
        : deal.winAge !== null && Math.floor(deal.winAge) === age ? 1 : 0;
      if (share > 0) {
        const amount = deal.value * weight * share;
        revenue += amount;
        winWeight += weight * share;
        if (deal.estimated) estimatedRevenue += amount;
        bySegment[deal.segment] = (bySegment[deal.segment] ?? 0) + amount;
      }
    }
    const effectivePopulation = weightSquares > 0 ? weightSum ** 2 / weightSquares : 0;
    minimumEffectivePopulation = Math.min(minimumEffectivePopulation, effectivePopulation);
    if (effectivePopulation < MIN_COHORT) ready = false;
    if (quality && affected / Math.max(1, population) > OUTLOOK_HISTORY_POLICY.maximumShare) ready = false;
    ages.push({ arr: weightSum ? revenue / weightSum : 0,
      probability: weightSum ? winWeight / weightSum : 0,
      estimatedArr: weightSum ? estimatedRevenue / weightSum : 0,
      bySegment: Object.fromEntries(Object.entries(bySegment).map(([key, amount]) => [key, weightSum ? amount / weightSum : 0])),
    });
  }
  const probability = ages.reduce((sum, age) => sum + age.probability, 0);
  // Age-specific mature samples can drift. Refuse an incoherent curve rather
  // than silently scaling it into a different model.
  ready = ready && recentWins >= MIN_WINS && probability <= 1;
  return { ages, ready, recentWins, minimumEffectivePopulation, lookbackDays, halfLifeDays };
}

// Conditional EXTRA creation in one existing value group. Reuse the same mature,
// recency-weighted closing-age calculation, with a group-specific denominator.
// This is a scenario, not a group forecast or an allocation of the target gap.
function creationArea(deals: readonly OutlookHistoryDeal[], asOf: Date, end: Date, start?: Date,
  quality?: ReturnType<typeof prepareOutlookHistoryQuality>) {
  const remaining = Math.max(0, (end.getTime() - asOf.getTime()) / 86_400_000);
  const windowDays = Math.min(30, Math.floor(remaining));
  const curve = futureArrByAge(deals, asOf, Math.min(366, remaining), quality);
  const recentFrom = subDays(startOfDay(asOf), 90);
  const count = (from: Date, to: Date) => deals.filter(deal => deal.createdAt! >= from && deal.createdAt! < to).length;
  const recentCount = count(recentFrom, startOfDay(asOf));
  const priorCount = count(subYears(recentFrom, 1), subYears(startOfDay(asOf), 1));
  const trendSupported = recentCount >= MIN_COHORT && priorCount >= MIN_COHORT;
  const scenario = (delayDays: number) => {
    const from = addDays(asOf, delayDays);
    const to = new Date(Math.min(addDays(from, windowDays).getTime(), end.getTime()));
    if (windowDays < 1 || to <= from || !curve.ready) return null;
    let arr = 0;
    let estimatedArr = 0;
    let wins = 0;
    let days = 0;
    for (let offset = 0; offset < windowDays; offset++) {
      const intervalFrom = addDays(from, offset);
      const intervalTo = new Date(Math.min(addDays(intervalFrom, 1).getTime(), to.getTime()));
      if (intervalTo <= intervalFrom) continue;
      const weight = (intervalTo.getTime() - intervalFrom.getTime()) / 86_400_000;
      const creationTime = (intervalFrom.getTime() + intervalTo.getTime()) / 2;
      const minAge = Math.max(0, ((start?.getTime() ?? asOf.getTime()) - creationTime) / 86_400_000);
      const maxAge = (end.getTime() - creationTime) / 86_400_000;
      days += weight;
      for (let age = Math.floor(minAge); age < Math.ceil(maxAge); age++) {
        const entry = curve.ages[age];
        const fraction = Math.max(0, Math.min(age + 1, maxAge) - Math.max(age, minAge));
        if (!entry) continue;
        arr += weight * fraction * entry.arr;
        estimatedArr += weight * fraction * entry.estimatedArr;
        wins += weight * fraction * entry.probability;
      }
    }
    return days > 0 && (!quality || arr === 0 || estimatedArr / arr <= OUTLOOK_HISTORY_POLICY.maximumShare)
      ? { from, to, days, arrPerExtraDeal: arr / days, inPeriodWinShare: wins / days } : null;
  };
  const current = remaining <= 366 ? scenario(0) : null;
  return { recentCount, priorCount, trend: trendSupported ? recentCount / priorCount : null,
    current, delayed: remaining <= 366 ? scenario(30) : null,
    observedWindowCreation: recentCount / 90 * windowDays,
    support: { recentWins: curve.recentWins, minimumEffectivePopulation: Math.floor(curve.minimumEffectivePopulation) },
  };
}

export function buildOutlookHistory(deals: readonly OutlookHistoryDeal[], asOf: Date, range?: { start: Date; end: Date },
  qualityOptions?: OutlookHistoryQualityOptions) {
  const source = [...new Map(deals.map(deal => [String(deal.id), deal])).values()];
  const clock = getOutlookClock(asOf, range);
  const quality = qualityOptions ? prepareOutlookHistoryQuality(source, asOf,
    Math.max(0, (clock.end.getTime() - asOf.getTime()) / 86_400_000), qualityOptions) : undefined;
  const unique = quality?.deals ?? source;
  const minimumDays = Math.max(0, ((range?.start.getTime() ?? asOf.getTime()) - asOf.getTime()) / 86_400_000);
  const missingCreated = unique.filter(deal => !finiteDate(deal.createdAt)).length;
  const futureCreated = unique.filter(deal => finiteDate(deal.createdAt) && deal.createdAt >= asOf).length;
  const valid = unique.filter(deal => finiteDate(deal.createdAt) && deal.createdAt < asOf);
  const oldest = valid.reduce<Date | null>((date, deal) => !date || deal.createdAt! < date ? deal.createdAt : date, null);
  const count = (from: Date, to: Date) => valid.filter(deal => deal.createdAt! >= from && deal.createdAt! < to).length;
  const recentFrom = subDays(startOfDay(asOf), 90);
  const recentCount = count(recentFrom, startOfDay(asOf));
  const priorRecentCount = count(subYears(recentFrom, 1), subYears(startOfDay(asOf), 1));
  const trend = priorRecentCount >= MIN_COHORT && recentCount >= MIN_COHORT
    ? recentCount / priorRecentCount : null;
  const fullHistory = Boolean(oldest && oldest <= subYears(startOfDay(asOf), 2));
  const qualityReady = missingCreated === 0 && futureCreated === 0;

  // Preserve the existing prior-year seasonal creation rate and recent
  // year-over-year trend. A second matching month is not needed to estimate
  // conversion: it now uses all mature, recency-weighted creation cohorts.
  const dailyCreationRates = new Map<string, number>();
  const priorYearDailyRates = new Map<string, number>();
  const maximumDays = Math.max(0, (clock.end.getTime() - asOf.getTime()) / 86_400_000);
  for (let offset = 0; offset <= Math.min(366, clock.daysRemaining); offset++) {
    const day = addDays(startOfDay(asOf), offset);
    const key = `${day.getFullYear()}-${day.getMonth()}`;
    if (priorYearDailyRates.has(key)) continue;
    const previousMonth = new Date(day.getFullYear() - 1, day.getMonth(), 1);
    const previousEnd = new Date(day.getFullYear() - 1, day.getMonth() + 1, 1);
    const lastYearCount = count(previousMonth, previousEnd);
    priorYearDailyRates.set(key, lastYearCount / getDaysInMonth(previousMonth));
    if (trend !== null && lastYearCount >= MIN_COHORT) {
      dailyCreationRates.set(key, lastYearCount / getDaysInMonth(previousMonth) * trend);
    }
  }
  // Approved Forecast-only fallback. Apply one measured pace to the entire
  // remaining window, not a hybrid of supported and guessed seasonal months.
  // Recent creation already reflects today's pace: never multiply by trend again.
  const recentCreationFallback = Boolean(qualityOptions?.allowRecentCreationFallback
    && fullHistory && trend !== null && recentCount >= MIN_COHORT
    && dailyCreationRates.size < priorYearDailyRates.size);
  if (recentCreationFallback) for (const key of priorYearDailyRates.keys()) {
    dailyCreationRates.set(key, recentCount / 90);
  }
  const creationBasis = recentCreationFallback ? "recent-90-days" as const : "seasonal" as const;

  const fullCohort = summarizeOutlookCohort(valid, asOf, clock.daysRemaining, minimumDays);
  const curve = futureArrByAge(valid, asOf, Math.min(366, maximumDays), quality);
  const invalidFutureInputs = valid.filter(deal => !Number.isFinite(deal.value) || deal.value < 0
    || (deal.isWon && (!finiteDate(deal.closedAt) || deal.closedAt < deal.createdAt!))).length;
  let expectedNewArr = 0;
  let expectedNewDeals = 0;
  let expectedWins = 0;
  let estimatedArr = 0;
  let expectedFirstWeekDeals = 0;
  const expectedBySegment: Record<string, number> = {};
  // Decompose the same integration; never change its baseline arithmetic.
  const creationMonths = new Map<string, { month: string; createdDeals: number; expectedWins: number; arr: number; dailyRate: number; priorYearDailyRate: number }>();
  const delayedCreation = [7, 14, 30].map(days => ({ days, arr: 0, createdDeals: 0 }));
  let ready = qualityReady && fullHistory && trend !== null && maximumDays > 0
    && maximumDays <= 366 && curve.ready && (quality ? quality.unresolvedIds.size === 0 : invalidFutureInputs / Math.max(1, valid.length) <= 0.05);
  // Project creation after the assessment instant, including the remaining
  // fraction of today. Daily arrivals use the midpoint of that interval;
  // within-day closing ages are interpolated instead of claiming exact times.
  for (let offset = 0; offset <= Math.min(366, clock.daysRemaining); offset++) {
    const day = addDays(startOfDay(asOf), offset);
    const from = Math.max(day.getTime(), asOf.getTime());
    const to = Math.min(addDays(day, 1).getTime(), clock.end.getTime());
    if (to <= from) continue;
    const rate = dailyCreationRates.get(`${day.getFullYear()}-${day.getMonth()}`);
    if (rate === undefined) { ready = false; continue; }
    const calendarDayMs = addDays(day, 1).getTime() - day.getTime();
    const volume = rate * (to - from) / calendarDayMs;
    const creationTime = (from + to) / 2;
    const monthKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}`;
    const month = creationMonths.get(monthKey) ?? { month: monthKey, createdDeals: 0, expectedWins: 0, arr: 0, dailyRate: rate,
      priorYearDailyRate: recentCreationFallback ? priorYearDailyRates.get(`${day.getFullYear()}-${day.getMonth()}`)! : rate / trend! };
    creationMonths.set(monthKey, month);
    month.createdDeals += volume;
    const remainingFractions = delayedCreation.map(delay => Math.max(0, to - Math.max(from, asOf.getTime() + delay.days * 86_400_000)) / (to - from));
    delayedCreation.forEach((delay, i) => { delay.createdDeals += volume * remainingFractions[i]; });
    const earliestAge = Math.max(0, ((range?.start.getTime() ?? asOf.getTime()) - creationTime) / 86_400_000);
    const latestAge = (clock.end.getTime() - creationTime) / 86_400_000;
    expectedNewDeals += volume;
    expectedFirstWeekDeals += rate * Math.max(0, Math.min(to, asOf.getTime() + 7 * 86_400_000) - from) / calendarDayMs;
    for (let age = Math.floor(earliestAge); age < Math.ceil(latestAge); age++) {
      const fraction = Math.max(0, Math.min(age + 1, latestAge) - Math.max(age, earliestAge));
      const entry = curve.ages[age];
      if (!entry || fraction <= 0) continue;
      expectedNewArr += volume * fraction * entry.arr;
      estimatedArr += volume * fraction * entry.estimatedArr;
      expectedWins += volume * fraction * entry.probability;
      month.arr += volume * fraction * entry.arr;
      month.expectedWins += volume * fraction * entry.probability;
      delayedCreation.forEach((delay, i) => { delay.arr += volume * fraction * entry.arr * remainingFractions[i]; });
      for (const [segment, arr] of Object.entries(entry.bySegment)) {
        expectedBySegment[segment] = (expectedBySegment[segment] ?? 0) + volume * fraction * arr;
      }
    }
  }
  const estimatedArrShare = expectedNewArr > 0 ? estimatedArr / expectedNewArr : 0;
  if (quality && estimatedArrShare > OUTLOOK_HISTORY_POLICY.maximumShare) ready = false;

  const segments = ["Small", "Medium", "Large"].map(segment => {
    const population = valid.filter(deal => deal.dimensions.segment === segment);
    const cohort = summarizeOutlookCohort(population, asOf, clock.daysRemaining, minimumDays);
    const windowDays = Math.max(1, Math.min(7, clock.daysRemaining));
    const weeklyCounts: number[] = [];
    // Twelve completed windows, including zero-creation windows.
    for (let i = 0; i < 12; i++) {
      const to = subDays(startOfDay(asOf), i * windowDays);
      const from = subDays(to, windowDays);
      weeklyCounts.push(population.filter(d => d.createdAt! >= from && d.createdAt! < to).length);
    }
    const sorted = weeklyCounts.toSorted((a, b) => a - b);
    const observedPace = sorted[Math.floor((sorted.length - 1) * 0.75)] ?? 0;
    let expectedCreation = 0;
    for (let offset = 1; offset <= Math.min(windowDays, clock.daysRemaining); offset++) {
      const day = addDays(startOfDay(asOf), offset);
      const from = new Date(day.getFullYear() - 1, day.getMonth(), 1);
      const to = new Date(day.getFullYear() - 1, day.getMonth() + 1, 1);
      expectedCreation += population.filter(deal => deal.createdAt! >= from && deal.createdAt! < to).length
        / getDaysInMonth(from) * (trend ?? 0);
    }
    const suggestedAdditional = ready && cohort.ready
      ? Math.max(0, Math.floor(observedPace - expectedCreation)) : 0;
    // The same observed weekly volume is evaluated at both ends of the
    // creation window; the later-creation amount is used for any visible path.
    const windowEnd = addDays(asOf, windowDays);
    const laterCohort = summarizeOutlookCohort(population, asOf,
      Math.max(0, (clock.end.getTime() - windowEnd.getTime()) / 86_400_000),
      Math.max(0, ((range?.start.getTime() ?? windowEnd.getTime()) - windowEnd.getTime()) / 86_400_000));
    const hasEstimatedTiming = population.some(deal => quality?.winAges.has(String(deal.id)));
    const creationScenario = qualityReady && cohort.ready && laterCohort.ready && !hasEstimatedTiming && clock.daysRemaining >= windowDays
      ? { volume: observedPace, delta: observedPace * Math.min(cohort.expectedArrPerCreatedDeal!, laterCohort.expectedArrPerCreatedDeal!) }
      : null;
    return { segment, ...cohort, creationScenario, observedPace, windowDays, expectedCreation, suggestedAdditional,
      suggestedDelta: suggestedAdditional * (cohort.expectedArrPerCreatedDeal ?? 0),
      forwardCreation: ready ? creationArea(population, asOf, clock.end, range?.start, quality) : null,
      ready: cohort.ready && qualityReady && clock.daysRemaining > 0
        && !hasEstimatedTiming };
  });

  const wonCycles = valid.filter(deal => deal.isWon && finiteDate(deal.closedAt)
    && deal.closedAt < asOf && deal.closedAt >= subMonths(asOf, 24) && deal.closedAt >= deal.createdAt!)
    .map(deal => (deal.closedAt!.getTime() - deal.createdAt!.getTime()) / 86_400_000).toSorted((a, b) => a - b);
  const cycle = wonCycles.length >= MIN_COHORT ? {
    wins: wonCycles.length,
    p25Days: wonCycles[Math.floor((wonCycles.length - 1) * 0.25)]!,
    medianDays: wonCycles[Math.floor((wonCycles.length - 1) * 0.5)]!,
    p75Days: wonCycles[Math.floor((wonCycles.length - 1) * 0.75)]!,
  } : null;
  return {
    ...clock, cycle, rows: unique.length, oldest, missingCreated, futureCreated,
    recentCount, priorRecentCount, trend, fullCohort, segments,
    future: {
      ready,
      ...(quality ? { quality: { ...quality.summary, estimatedArrShare, estimatedArr, creationBasis,
        recentDailyCreationRate: recentCreationFallback ? recentCount / 90 : null } } : {}),
      value: ready ? expectedNewArr : null,
      createdDeals: ready ? expectedNewDeals : null,
      expectedFirstWeekDeals: ready ? expectedFirstWeekDeals : null,
      expectedWins: ready ? expectedWins : null,
      bySegment: ready ? expectedBySegment : {},
      diagnostics: ready ? {
        creationMonths: [...creationMonths.values()], delayedCreation,
        inPeriodWinShare: expectedNewDeals > 0 ? expectedWins / expectedNewDeals : 0,
        recentCreation: recentCount, priorYearCreation: priorRecentCount, creationTrend: trend!,
        // Same conversion and seasonality, without the already-applied trend.
        arrAtPriorYearCreationPace: expectedNewArr / trend!,
      } : null,
      support: { recentWins: curve.recentWins, minimumEffectivePopulation: Number.isFinite(curve.minimumEffectivePopulation) ? Math.floor(curve.minimumEffectivePopulation) : 0,
        lookbackDays: curve.lookbackDays, halfLifeDays: curve.halfLifeDays, invalidInputs: invalidFutureInputs },
      reason: maximumDays <= 0 ? "The selected period has ended."
        : maximumDays > 366 ? "Future creation is supported up to one year ahead."
        : !qualityReady ? "Some creation dates are missing or ahead of the assessment time."
        : !fullHistory ? "Two years of creation history are needed to check seasonality."
          : trend === null ? "Recent and prior-year creation volumes are too small to establish a trend."
            : quality && (quality.unresolvedIds.size > 0 || estimatedArrShare > OUTLOOK_HISTORY_POLICY.maximumShare)
              ? "Comparable history cannot support the missing amounts or win timing within the 5% estimation limit."
            : !quality && invalidFutureInputs / Math.max(1, valid.length) > 0.05 ? "Too many historical amounts or won dates are invalid."
              : !curve.ready ? "Too few mature observations or inconsistent conversion across closing ages."
                : !ready ? "Seasonal creation history does not support every remaining month."
                  : null,
    },
  };
}

/** Only additional opportunities count as an incremental lever. */
export function quantifyNewPipelineScenario(
  cohort: ReturnType<typeof summarizeOutlookCohort>, additionalDeals: number, baseline: number, target: number,
) {
  if (!cohort.ready || !Number.isInteger(additionalDeals) || additionalDeals < 0 || additionalDeals > 10_000) return null;
  const delta = additionalDeals * cohort.expectedArrPerCreatedDeal!;
  return { delta, wins: additionalDeals * cohort.probability!, outcome: baseline + delta,
    targetShare: target > 0 ? (baseline + delta) / target : null };
}

/** Decision policy for this isolated experiment, never a forecast/model override. */
export function prioritizeOutlook<T extends { impact: number }>(input: {
  current: number; target: number; closed: boolean; futurePeriod: boolean; futureIncluded?: boolean;
  daysRemaining: number; cycle: { medianDays: number } | null;
  paths: T[]; newCreation: { delta: number; volume: number } | null;
  downsideDelta: number | null;
  history?: Pick<ReturnType<typeof buildOutlookHistory>, "future"> & Partial<Pick<ReturnType<typeof buildOutlookHistory>, "segments">> | null;
}) {
  const gap = Math.max(0, input.target - input.current);
  // Two percentage points, or the smaller amount that would close the gap.
  const minimumImpact = Math.min(input.target * 0.02, gap || Infinity);
  const paths = input.closed || input.current >= input.target ? []
    : input.paths.filter(path => path.impact >= minimumImpact).toSorted((a, b) => b.impact - a.impact);
  const creation = !input.closed && input.current < input.target && input.newCreation
    && input.newCreation.delta >= minimumImpact ? input.newCreation : null;
  const delta = paths.reduce((sum, path) => sum + path.impact, 0) + (creation?.delta ?? 0);
  const downside = input.downsideDelta === null ? null : Math.max(0, input.current + input.downsideDelta);
  const shortWindow = input.cycle !== null && input.daysRemaining < input.cycle.medianDays;
  const status: "closed" | "exposed" | "above" | "conditional-route" | "incomplete" | "limited-recovery" | "gap" = input.closed ? "closed" : input.current >= input.target
    ? downside !== null && downside < input.target ? "exposed" : "above"
    : input.current + delta >= input.target ? "conditional-route"
      : input.futureIncluded ? shortWindow ? "limited-recovery" : "gap"
        : input.futurePeriod || !shortWindow ? "incomplete" : "limited-recovery";
  const future = input.history?.future;
  const diagnostics = future?.ready ? future.diagnostics : null;
  const futureArr = diagnostics ? future!.value! : 0;
  // Dependence on future creation is not recoverable upside. Compare it with
  // identified deal levers only after checking whether those close the gap.
  const futureMaterial = futureArr >= minimumImpact && futureArr > 0;
  const creationWindow = input.cycle !== null && !shortWindow;
  const primaryFocus = input.closed ? "closed"
    : !input.futureIncluded ? "establish-outlook"
    : status === "exposed" ? "protect-existing"
    : status === "above" ? futureMaterial && futureArr > input.current - input.target ? "sustain-creation" : "maintain"
    : input.cycle === null && futureMaterial && delta < gap && futureArr > delta ? "establish-timing"
    : creationWindow && futureMaterial && delta < gap && futureArr > delta ? "build-pipeline"
    : paths.length ? "existing-deals" : "unresolved";
  const requirement = diagnostics && futureArr > 0 && gap > 0 ? {
    additionalArr: gap,
    uplift: gap / futureArr,
    additionalDeals: future!.createdDeals! * gap / futureArr,
    afterExistingDeals: Math.max(0, gap - delta) / futureArr,
    delays: diagnostics.delayedCreation.filter(delay => delay.days < input.daysRemaining).map(delay => ({
      days: delay.days, availableArr: delay.arr,
      uplift: delay.arr > 0 ? gap / delay.arr : null,
    })),
  } : null;
  // A timing warning needs an economic difference, not merely a later date.
  const timing = requirement?.delays.toReversed().find(delay => futureArr - delay.availableArr >= minimumImpact) ?? null;
  const pipeline = diagnostics ? { expectedArr: futureArr, createdDeals: future!.createdDeals!,
    ...(future?.quality ? { creationBasis: future.quality.creationBasis } : {}),
    expectedWins: future!.expectedWins!, inPeriodWinShare: diagnostics.inPeriodWinShare,
    creationTrend: diagnostics.creationTrend, recentCreation: diagnostics.recentCreation,
    priorYearCreation: diagnostics.priorYearCreation, cycleDays: input.cycle?.medianDays ?? null,
    trendArrDifference: futureArr - diagnostics.arrAtPriorYearCreationPace,
    sizeContributions: Object.entries(future!.bySegment).map(([group, arr]) => ({ group, arr })).sort((a, b) => b.arr - a.arr),
    creationMonths: diagnostics.creationMonths, requirement, timing,
  } : null;
  const areas = (input.history?.segments ?? []).flatMap(segment => {
    const area = segment.forwardCreation;
    if (!diagnostics || !area) return [];
    const yieldNow = area.current?.arrPerExtraDeal ?? 0;
    const yieldLater = area.delayed?.arrPerExtraDeal ?? 0;
    // Rank a comparable increase equal to recent observed volume, not raw deal
    // value or the largest existing contribution. This does not prove capacity.
    const comparableImpact = area.observedWindowCreation * yieldNow;
    // Avoid ceil(20.000000000000004) adding a fictitious whole opportunity.
    const required = (amount: number, yieldPerDeal: number) => Math.ceil(Number((amount / yieldPerDeal).toPrecision(12)));
    return [{ group: segment.segment, ...area, comparableImpact,
      additionalDeals: gap > 0 && yieldNow > 0 ? required(gap, yieldNow) : null,
      additionalDealsAfterExisting: gap > 0 && yieldNow > 0 ? required(Math.max(0, gap - delta), yieldNow) : null,
      additionalDealsIfDelayed: gap > 0 && yieldLater > 0 ? required(gap, yieldLater) : null,
      material: comparableImpact >= minimumImpact && area.recentCount >= MIN_COHORT,
    }];
  });
  const rankedAreas = primaryFocus === "build-pipeline" ? areas.filter(area => area.material && area.additionalDeals !== null)
    .toSorted((a, b) => b.comparableImpact - a.comparableImpact).slice(0, 3) : [];
  return { status, paths, creation, minimumImpact, delta, scenario: input.current + delta, downside, shortWindow,
    primaryFocus, pipeline, areas, rankedAreas,
    creationImmaterial: input.newCreation !== null && input.newCreation.delta < minimumImpact };
}
